import React from 'react';
import { Link } from 'react-router-dom';
import Logo from '../components/Logo';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <Logo size="sm" />
            </Link>
            <Link
              to="/"
              className="text-sm font-medium text-primary-600 hover:text-primary-500"
            >
              Back
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white shadow-sm rounded-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-sm text-gray-500 mb-8">Effective Date: November 10, 2025</p>

          <div className="prose prose-blue max-w-none">
            <p className="text-gray-700 mb-6">
              Welcome to Zest AI Marketing Content Generation Platform (hereinafter referred to as "we", "our", or "Zest"). We value and respect your privacy. This Privacy Policy explains how we collect, use, disclose, and protect your personal information.
            </p>

            <p className="text-gray-700 mb-8">
              By using our services, you agree to the practices described in this Privacy Policy. If you do not agree with this policy, please do not use our services.
            </p>

            {/* 1. Information We Collect */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Information We Collect</h2>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">1.1 Information You Provide</h3>
              <p className="text-gray-700 mb-3">When you use Zest, we may collect the following information:</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>Account Information</strong>: Name, email address, password</li>
                <li><strong>Payment Information</strong>: Credit card information, billing address (securely processed by third-party payment processors)</li>
                <li><strong>Profile Information</strong>: Any other information you choose to provide</li>
                <li><strong>Content Data</strong>: Content and data you create, upload, or generate</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">1.2 Automatically Collected Information</h3>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>Usage Data</strong>: How you use our services</li>
                <li><strong>Device Information</strong>: IP address, browser type, operating system</li>
                <li><strong>Location Information</strong>: Approximate geographic location based on IP address</li>
                <li><strong>Cookies</strong>: Small files used to identify your browser or device</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">1.3 Third-Party Information</h3>
              <p className="text-gray-700 mb-4">
                If you choose to log in through third-party services (such as Google), we receive basic profile information (name, email address).
              </p>
            </section>

            {/* 2. How We Use Your Information */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. How We Use Your Information</h2>
              <p className="text-gray-700 mb-3">We use the collected information for:</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Providing and improving our services</li>
                <li>Processing transactions and sending related notifications</li>
                <li>Providing customer support</li>
                <li>Sending service-related announcements and updates</li>
                <li>Detecting, preventing, and resolving technical or security issues</li>
                <li>Analyzing usage trends and user preferences</li>
              </ul>
            </section>

            {/* 3. How We Share Your Information */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Share Your Information</h2>
              <p className="text-gray-700 mb-3">
                <strong>We do not sell your personal information.</strong> We may share your information in the following circumstances:
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.1 Service Providers</h3>
              <p className="text-gray-700 mb-3">We share information with third-party service providers that help us operate our business:</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Cloud storage services (AWS, Google Cloud)</li>
                <li>Payment processing (Stripe)</li>
                <li>Analytics services (Google Analytics)</li>
                <li>Email services</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.2 Legal Requirements</h3>
              <p className="text-gray-700 mb-4">
                When we believe in good faith that disclosure is necessary, we may disclose your information to comply with legal processes, court orders, or government requests.
              </p>
            </section>

            {/* 4. Data Security */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data Security</h2>
              <p className="text-gray-700 mb-3">We take reasonable measures to protect your personal information:</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>Encryption</strong>: Using SSL/TLS to encrypt data in transit</li>
                <li><strong>Access Controls</strong>: Limiting access to personal information</li>
                <li><strong>Secure Storage</strong>: Storing data on secure servers</li>
                <li><strong>Regular Audits</strong>: Regularly reviewing our security practices</li>
              </ul>
              <p className="text-gray-700 mb-4">
                However, please note that no method of Internet transmission or electronic storage is 100% secure.
              </p>
            </section>

            {/* 5. Your Rights and Choices */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Your Rights and Choices</h2>
              <p className="text-gray-700 mb-3">You have the following rights:</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>Access and Update</strong>: Access and correct your personal information</li>
                <li><strong>Deletion</strong>: Request deletion of your personal information</li>
                <li><strong>Data Portability</strong>: Receive a copy of your data</li>
                <li><strong>Marketing Communications</strong>: Opt out of receiving marketing emails</li>
                <li><strong>Cookie Management</strong>: Manage cookies through browser settings</li>
              </ul>
              <p className="text-gray-700 mb-4">
                To exercise these rights, please contact us at:
                <a href="mailto:reception@getaizest.com" className="text-primary-600 hover:text-primary-500 ml-1">
                  reception@getaizest.com
                </a>
              </p>
            </section>

            {/* 6. Cookies and Tracking Technologies */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Cookies and Tracking Technologies</h2>
              <p className="text-gray-700 mb-3">We use cookies and similar technologies to:</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>Essential Cookies</strong>: Make the website function properly</li>
                <li><strong>Performance Cookies</strong>: Analyze website usage</li>
                <li><strong>Functional Cookies</strong>: Remember your preferences</li>
              </ul>
            </section>

            {/* 7. Children's Privacy */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Children's Privacy</h2>
              <p className="text-gray-700 mb-4">
                Our services are not directed to children under 13. We do not knowingly collect personal information from children under 13.
              </p>
            </section>

            {/* 8. Changes to This Privacy Policy */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Effective Date".
              </p>
            </section>

            {/* 9. Contact Us */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Contact Us</h2>
              <p className="text-gray-700 mb-3">
                If you have any questions, concerns, or requests about this Privacy Policy, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700 mb-2"><strong>Zest AI Team</strong></p>
                <p className="text-gray-700 mb-1">
                  Email:
                  <a href="mailto:reception@getaizest.com" className="text-primary-600 hover:text-primary-500 ml-1">
                    reception@getaizest.com
                  </a>
                </p>
                <p className="text-gray-700">
                  Website:
                  <a href="https://getaizest.com" className="text-primary-600 hover:text-primary-500 ml-1">
                    https://getaizest.com
                  </a>
                </p>
              </div>
            </section>

            <div className="border-t pt-6 mt-8">
              <p className="text-sm text-gray-500">Last Updated: November 10, 2025</p>
              <p className="text-sm text-gray-500">Version: 1.0</p>
              <p className="text-sm text-gray-500 mt-4">
                © 2025 Zest AI. All rights reserved.
              </p>
            </div>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 text-center">
          <Link to="/terms" className="text-sm text-gray-600 hover:text-gray-900 mx-4">
            Terms of Service
          </Link>
          <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900 mx-4">
            Login
          </Link>
          <Link to="/register" className="text-sm text-gray-600 hover:text-gray-900 mx-4">
            Register
          </Link>
        </div>
      </main>
    </div>
  );
};

export default Privacy;
